import { ComponentPropsWithoutRef, forwardRef } from 'react';

import { Size as ButtonSize, Variation } from '../Button/Button';
import { Icon, IconSource, Size as IconSize } from '../Icon/Icon';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { StyledIconButton } from './IconButton.styles';

export interface Props extends ComponentPropsWithoutRef<'button'> {
	inverted?: boolean;
	rounded?: boolean;
	size?: ButtonSize;
	source: IconSource;
	variation?: Variation | 'content-only';
	isLoading?: boolean;
	pressed?: boolean;
	/**
	 * Stretch the button to its parent's content height (`-webkit-fill-available`). Opt-in for
	 * cases like overlay buttons on a fixed-height image or close buttons that should match a
	 * banner/input row. Off by default — turning it on inside an unconstrained flex container
	 * propagates the fill upward and balloons the button to viewport height in Safari.
	 */
	fillHeight?: boolean;
}

const iconSizeBySize: Record<ButtonSize, IconSize> = {
	'extra-small': 'extra-small',
	small: 'extra-small',
	medium: 'small',
	large: 'medium',
};

const IconButton = forwardRef<HTMLButtonElement, Props>(
	(
		{
			size = 'medium',
			source,
			type = 'button',
			variation = 'primary',
			isLoading = false,
			rounded,
			pressed,
			fillHeight,
			...rest
		},
		ref
	) => (
		<StyledIconButton
			isLoading={isLoading}
			size={size}
			type={type}
			/* We add a custom variant on the IconButton since it doesn't relate to the actual button styling */
			variation={variation === 'content-only' ? 'secondary' : variation}
			contentOnly={variation === 'content-only'}
			fillHeight={fillHeight}
			rounded={rounded}
			ref={ref}
			pressed={pressed}
			aria-pressed={pressed}
			{...rest}
		>
			{isLoading ? (
				<LoadingSpinner tw="absolute text-current" size={rounded ? iconSizeBySize[size] : size} />
			) : (
				<Icon color="currentColor" source={source} size={rounded ? iconSizeBySize[size] : size} />
			)}
		</StyledIconButton>
	)
);

IconButton.displayName = 'IconButton';

export { IconButton };
